import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiRequestError } from "../../lib/api";
import { useSignedInContext } from "../auth/useSignedInContext";
import type { SourceDetail } from "./types";

const PROCESSING_LABELS: Record<string, string> = {
  uploaded: "Hochgeladen, wartet auf Verarbeitung",
  queued: "In Warteschlange",
  processing: "Wird verarbeitet",
  ready: "Verarbeitet",
  failed: "Verarbeitung fehlgeschlagen",
  needs_ocr: "Bildbasiertes PDF — OCR noch nicht verfügbar, Text kann nicht extrahiert werden",
  needs_transcript: "Transkript erforderlich"
};

const REPROCESSABLE = new Set(["uploaded", "failed", "needs_ocr"]);

function formatTime(seconds: number | null): string | null {
  if (seconds === null) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function SourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { member } = useSignedInContext();
  const [detail, setDetail] = useState<SourceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMessage, setReprocessMessage] = useState<string | null>(null);
  const [ragError, setRagError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    api
      .get<SourceDetail>(`/api/v1/sources/${id}`)
      .then(setDetail)
      .catch((err: unknown) => {
        if (err instanceof ApiRequestError && err.status === 404) {
          setError("Quelle nicht gefunden.");
          return;
        }
        setError("Quelle konnte nicht geladen werden.");
      });
  }, [id]);

  useEffect(() => load(), [load]);

  async function onPublish() {
    if (!id) return;
    await api.post(`/api/v1/sources/${id}/publish`);
    load();
  }
  async function onWithdraw() {
    if (!id) return;
    await api.post(`/api/v1/sources/${id}/withdraw`);
    load();
  }
  async function onReprocess() {
    if (!id) return;
    setReprocessing(true);
    setReprocessMessage(null);
    try {
      await api.post(`/api/v1/sources/${id}/reprocess`);
      setReprocessMessage("Neu eingeplant — Status aktualisiert sich, sobald die Verarbeitung abgeschlossen ist.");
      load();
    } finally {
      setReprocessing(false);
    }
  }
  async function onRagApprove() {
    if (!id) return;
    setRagError(null);
    try {
      await api.post(`/api/v1/sources/${id}/rag-approve`);
      load();
    } catch (err) {
      setRagError(err instanceof ApiRequestError ? err.message : "Freigabe für den Wissenschat fehlgeschlagen.");
    }
  }
  async function onRagWithdraw() {
    if (!id) return;
    await api.post(`/api/v1/sources/${id}/rag-withdraw`);
    load();
  }

  if (error) {
    return (
      <section className="pw-card">
        <p style={{ margin: 0 }}>{error}</p>
        <button type="button" className="pw-button" onClick={() => void navigate("/library")} style={{ marginTop: "0.8rem" }}>
          Zurück zur Bibliothek
        </button>
      </section>
    );
  }
  if (!detail) return <p style={{ color: "var(--pw-color-text-muted)" }}>Lädt …</p>;

  const { source, version, segments } = detail;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <article className="pw-card">
        <h1 style={{ margin: "0 0 0.3rem", fontSize: "1.4rem", fontWeight: 600, letterSpacing: "-0.02em" }}>{source.title}</h1>
        <p style={{ margin: "0 0 0.8rem", fontSize: "0.82rem", color: "var(--pw-color-text-muted)" }}>
          {source.author ? `${source.author} · ` : ""}
          {source.language.toUpperCase()}
          {source.region ? ` · ${source.region}` : ""}
          {source.originalUrl && (
            <>
              {" · "}
              <a href={source.originalUrl} target="_blank" rel="noreferrer noopener">
                Ursprungslink
              </a>
            </>
          )}
        </p>

        {version && (
          <div className="pw-alert pw-alert--info" style={{ marginBottom: "0.8rem", display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap" }}>
            <span style={{ flex: 1, minWidth: "200px" }}>
              Verarbeitungsstatus: {PROCESSING_LABELS[version.processingStatus] ?? version.processingStatus}
              {version.errorMessage && <> — {version.errorMessage}</>}
            </span>
            {member.role === "admin" && REPROCESSABLE.has(version.processingStatus) && (
              <button type="button" className="pw-button" onClick={() => void onReprocess()} disabled={reprocessing}>
                {reprocessing ? "Wird eingeplant …" : "Erneut verarbeiten"}
              </button>
            )}
          </div>
        )}
        {reprocessMessage && (
          <div className="pw-alert pw-alert--info" style={{ marginBottom: "0.8rem" }}>
            {reprocessMessage}
          </div>
        )}

        {source.type === "pdf" && (
          <a href={`/api/v1/sources/${source.id}/original`} target="_blank" rel="noreferrer" className="pw-button pw-button--primary" style={{ marginBottom: "0.8rem" }}>
            Original-PDF öffnen
          </a>
        )}

        {member.role === "admin" && (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <span
              style={{
                fontSize: "0.78rem",
                padding: "0.3rem 0.7rem",
                borderRadius: "var(--pw-radius-sm)",
                background: "var(--pw-color-surface-soft)",
                color: "var(--pw-color-text-muted)"
              }}
            >
              Status: {source.publishStatus}
            </span>
            {source.publishStatus !== "published" && (
              <button type="button" className="pw-button pw-button--primary" onClick={() => void onPublish()}>
                Freigeben
              </button>
            )}
            {source.publishStatus === "published" && (
              <button type="button" className="pw-button" onClick={() => void onWithdraw()}>
                Zurückziehen
              </button>
            )}
          </div>
        )}

        {member.role === "admin" && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.6rem" }}>
            <span
              style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", borderRadius: "var(--pw-radius-sm)", background: "var(--pw-color-surface-soft)", color: "var(--pw-color-text-muted)" }}
            >
              Wissenschat: {source.ragApproved ? `freigegeben (Index: ${version?.indexStatus ?? "—"})` : "nicht freigegeben"}
            </span>
            {!source.ragApproved ? (
              <button type="button" className="pw-button pw-button--primary" onClick={() => void onRagApprove()} disabled={version?.processingStatus !== "ready"}>
                Für Wissenschat freigeben
              </button>
            ) : (
              <button type="button" className="pw-button" onClick={() => void onRagWithdraw()}>
                Aus Wissenschat zurückziehen
              </button>
            )}
          </div>
        )}
        {ragError && (
          <div className="pw-alert pw-alert--error" style={{ marginTop: "0.6rem" }}>
            {ragError}
          </div>
        )}
      </article>

      {segments.length > 0 && (
        <section className="pw-card">
          <p style={{ margin: "0 0 0.7rem", fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--pw-color-text-muted)" }}>
            Inhalt ({segments.length} Abschnitte)
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
            {segments.map((seg, i) => (
              <div key={seg.id} style={{ padding: "0.7rem 0.9rem", background: "var(--pw-color-surface-flat)", border: "1px solid var(--pw-color-surface-flat-border)", borderRadius: "var(--pw-radius-sm)" }}>
                <p style={{ margin: "0 0 0.3rem", fontSize: "0.72rem", color: "var(--pw-color-text-subtle)", fontFamily: "var(--pw-font-mono)" }}>
                  {seg.heading ?? `Abschnitt ${i + 1}`}
                  {seg.pdfPageFrom && ` · Seite ${seg.pdfPageFrom}`}
                  {seg.timeStartSeconds !== null && ` · ${formatTime(seg.timeStartSeconds)}–${formatTime(seg.timeEndSeconds)}`}
                </p>
                <p style={{ margin: 0, fontSize: "0.88rem", lineHeight: 1.6, color: "var(--pw-color-text-body)" }}>{seg.textPreview}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
